/**
 * The automation-category vocabulary, in the order the product reads it.
 *
 * The hub stores a category key on every automation (`lighting`, `climate`,
 * `wellness`, …) and derives the categories a *device* belongs to. Every screen
 * that shows, sorts or filters by that key reads this table instead of its own
 * copy, so adding a category is one edit: the picker, the settings filter, the
 * automation cards and every device list learn the same word in the same place.
 *
 * The list is mirrored in the hub
 * (`otto/src/device/categories/automationCategories.ts`) — the two repos share
 * types, not runtime tables — and both copies carry a test asserting the order.
 * Change both in the same commit.
 *
 * `irrigation` is a legacy stored value — those rows group under Water.
 */
import { tt } from './consoleText';
import type { Bilingual } from '../config/bilingual';

export interface AutomationCategoryOption {
  value: string;
  label: Bilingual;
}

/**
 * Reading order — the owner's list. Security leads; `scheduling` trails because
 * it is a mechanism, not a room. `custom` is the bucket for an automation with
 * no category, so it sorts last.
 */
export const AUTOMATION_CATEGORY_OPTIONS: AutomationCategoryOption[] = [
  { value: 'security', label: { en: 'Security', sl: 'Varnost' } },
  { value: 'lighting', label: { en: 'Lights', sl: 'Luči' } },
  { value: 'shading', label: { en: 'Shades', sl: 'Senčila' } },
  { value: 'climate', label: { en: 'Climate', sl: 'Klima' } },
  { value: 'appliances', label: { en: 'Appliances', sl: 'Aparati' } },
  { value: 'wellness', label: { en: 'Wellness', sl: 'Dobro počutje' } },
  { value: 'energy', label: { en: 'Energy', sl: 'Energija' } },
  { value: 'water', label: { en: 'Water', sl: 'Voda' } },
  { value: 'scheduling', label: { en: 'Scheduling', sl: 'Urniki' } },
  { value: 'custom', label: { en: 'Custom', sl: 'Po meri' } },
];

/** The order the categories are read in, keys only. */
export const AUTOMATION_CATEGORY_ORDER: string[] = AUTOMATION_CATEGORY_OPTIONS.map((option) => option.value);

/** The category key an automation is presented under. */
export function normalizeAutomationCategory(category?: string | null): string {
  return category === 'irrigation' ? 'water' : (category ?? 'custom');
}

/**
 * Position in the reading order. `irrigation` ranks as Water; anything unknown
 * sorts last so a new key never jumps the queue.
 */
export function automationCategoryRank(category?: string | null): number {
  const index = AUTOMATION_CATEGORY_ORDER.indexOf(normalizeAutomationCategory(category));
  return index === -1 ? AUTOMATION_CATEGORY_ORDER.length : index;
}

/** Orders two category keys the way the product reads them. */
export function compareAutomationCategories(left?: string | null, right?: string | null): number {
  return automationCategoryRank(left) - automationCategoryRank(right);
}

/** The given category keys, de-duplicated and in reading order. */
export function sortAutomationCategories(categories: Iterable<string | null | undefined>): string[] {
  const seen = new Set<string>();
  for (const category of categories) {
    seen.add(normalizeAutomationCategory(category));
  }
  return [...seen].sort(compareAutomationCategories);
}

/** The category's word in the reader's language; an unknown key stays as it came. */
export function automationCategoryLabel(category?: string | null): string {
  const key = normalizeAutomationCategory(category);
  const option = AUTOMATION_CATEGORY_OPTIONS.find((entry) => entry.value === key);
  return option ? tt(option.label.en, option.label.sl) : (category ?? '');
}

/** The chip colour a category's badge carries. */
export function automationCategoryChipClass(category?: string | null): string {
  const map: Record<string, string> = {
    lighting: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
    shading: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
    climate: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
    security: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    scheduling: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
    water: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300',
    appliances: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
    wellness: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
    energy: 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300',
    custom: 'bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-300',
  };
  return map[normalizeAutomationCategory(category)] ?? map.custom;
}
